import * as XLSX from 'xlsx';
import { Empty, Flex } from 'antd';
import tableDesign from 'themes/table';
import MainCard from 'components/MainCard';
import { useCallback, useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import ExportButton from 'components/button/ExportButton';
import { TanStackColumnFilter } from 'components/@extended/ColumnFilter';
import { TanStackColumnSorting } from 'components/@extended/SortingIconGroup';
import { enhanceColumnsWithFiltering, generateExcelFileName } from 'utils/utils';
import { SelectVisibleColumnV8 } from 'components/@extended/SelectVisibleColumn';
import { TranStackPagination } from 'components/LynDataTable/DataTablePagination';
import { Box, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip } from '@mui/material';
import { flexRender, getCoreRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';

const tableVariants = {
	default: {
		table: {
			width: '100%',
			height: 'auto',
			// borderCollapse: 'collapse',

			'& td, th': {
				borderRight: '1px solid',
				borderColor: tableDesign.borderColor.primary,
			},

			'& th': {
				'&:first-of-type': { borderLeft: `1px solid ${tableDesign.borderColor.primary}` },
			},
		},
		tableMainCard: {
			mt: 2,
			width: '100%',
			overflow: 'hidden',
			borderRadius: tableDesign.borderRadius.primary,
			borderColor: tableDesign.borderColor.primary,
			border: 'none',
		},
		tableContainer: {
			width: '100%',
			display: 'block',
			overflowX: 'auto',
			position: 'relative',
			maxHeight: `calc(100svh - 357px)`,

			'& td, & th': { whiteSpace: 'nowrap' },
		},
		tableBodyCell: {
			overflow: 'hidden',
			textOverflow: 'ellipsis',
			borderBottom: `1px solid ${tableDesign.borderColor.primary}`,
			padding: tableDesign.padding.primary,
			fontSize: tableDesign.fontSize.primary,
			fontWeight: tableDesign.fontWeight.primary,
			color: tableDesign.color.secondary,

			'&:first-of-type': { borderLeft: '1px solid', borderColor: tableDesign.borderColor.primary },
		},
		tableHeaderCell: {
			top: '40px',
			height: '40px',
			position: 'sticky',
			padding: tableDesign.padding.default,
			backgroundColor: tableDesign.background.primary,

			'& .ant-input-affix-wrapper': { border: '1px solid #ced4da !important' },
		},
		tableHeaderSelectionCell: (header) => ({
			color: tableDesign.color.primary,
			padding: tableDesign.padding.secondary,
			fontSize: tableDesign.headerFontSize.primary,
			backgroundColor: tableDesign.background.primary,
			fontWeight: tableDesign.headerFontWeight.primary,

			...(header.id.toLowerCase() === 'selection' && { width: '52px' }),
		}),
		tableHeaderRow: {
			height: '40px',
		},
		tableHeaderSelectionBox: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
		tableBodyRow: { cursor: 'pointer' },
		emptyTableBox: {
			width: '100%',
			height: '350px',
			display: 'flex',
			alignItems: 'center',
			flexDirection: 'column',
			justifyContent: 'center',
		},
		paginationBox: {
			width: '100%',
			backgroundColor: tableDesign.background.primary,
		},
		pagination: {
			'&.Mui-disabled': { color: 'red' },
			'&.MuiTablePagination-root': { color: tableDesign.color.primary },
			'.MuiTablePagination-selectIcon': { color: tableDesign.color.primary },
		},
	},
};

const ComptrollerDataTableV8 = ({
	DATA,
	deleteHandle,
	COLUMNS = [],
	selectedTabName,
	rowSelection = {},
	bulkDelete = true,
	screen = 'default',
	variant = 'default',
	submitHandle = () => {},
	xlsxFileName = 'lynorg',
	setFilterData = () => {},
	onClickHandler = () => {},
	setRowSelection = () => {},
	activeDeactivateHandle = () => {},
}) => {
	const theme = useTheme();

	const table = useReactTable({
		data: DATA,
		columns: enhanceColumnsWithFiltering(COLUMNS),
		initialState: {
			pagination: {
				pageIndex: 0,
				pageSize: 15,
			},
		},
		filterFns: {
			textFilter: (row, columnId, filterValue) => {
				if (!filterValue) return true;

				const cellValue = String(row.getValue(columnId) || '').toLowerCase();
				const searchValue = String(filterValue).toLowerCase();

				return cellValue.includes(searchValue);
			},
			numberFilter: (row, columnId, filterValue) => {
				if (filterValue == null || filterValue === '') return true;

				const cellValue = Number(row.getValue(columnId));
				const filterNumber = Number(filterValue);

				return cellValue === filterNumber;
			},
		},
		globalFilterFn: 'textFilter',
		enableRowSelection: true,
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
	});

	const dataExport = useCallback(() => {
		const headerArr = table
			?.getVisibleLeafColumns()
			?.map((item) => item.id)
			?.filter((item) => item !== 'Selection' && item !== 'activationStatus' && item !== 'Action' && item !== 'Status');

		const mainColumn = COLUMNS?.filter((item) => item.id !== 'Selection' && item.key !== 'activationStatus' && item.key !== 'Action').reduce(
			(acc, col) => {
				acc[col.key] = col.header;
				return acc;
			},
			{}
		);

		const Header = headerArr.map((item) => {
			if (mainColumn?.[item]) {
				return mainColumn?.[item];
			} else {
				return item;
			}
		});

		const rows = table.getFilteredRowModel().rows;
		const rowsArr = rows.map((row) => {
			const rowObj = {};
			headerArr.forEach((colId) => {
				rowObj[colId] = row.getValue(colId);
			});
			return rowObj;
		});

		const wb = XLSX.utils.book_new();
		const ws = XLSX.utils.json_to_sheet([]);
		XLSX.utils.sheet_add_aoa(ws, [Header]);
		XLSX.utils.sheet_add_json(ws, rowsArr, { origin: 'A2', skipHeader: true });
		XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
		XLSX.writeFile(wb, `${generateExcelFileName(xlsxFileName)}.xlsx`);
	}, []);

	useEffect(() => {
		setRowSelection(table.getSelectedRowModel().flatRows);
	}, [table.getSelectedRowModel().flatRows]);

	useEffect(() => {
		if (table?.getFilteredRowModel()?.flatRows?.length > 0) {
			const filterData = table.getFilteredRowModel().flatRows?.map((row) => row.original);

			setFilterData(filterData);
		}
	}, [table?.getFilteredRowModel()?.flatRows]);

	const styles = {
		table: { ...tableVariants.default.table, ...tableVariants[variant]?.table },
		pagination: { ...tableVariants.default.pagination, ...tableVariants[variant]?.pagination },
		tableBodyRow: { ...tableVariants.default.tableBodyRow, ...tableVariants[variant]?.tableBodyRow },
		emptyTableBox: { ...tableVariants.default.emptyTableBox, ...tableVariants[variant]?.emptyTableBox },
		tableMainCard: { ...tableVariants.default.tableMainCard, ...tableVariants[variant]?.tableMainCard },
		paginationBox: { ...tableVariants.default.paginationBox, ...tableVariants[variant]?.paginationBox },
		tableBodyCell: { ...tableVariants.default.tableBodyCell, ...tableVariants[variant]?.tableBodyCell },
		tableContainer: { ...tableVariants.default.tableContainer, ...tableVariants[variant]?.tableContainer },
		tableHeaderRow: { ...tableVariants.default.tableHeaderRow, ...tableVariants[variant]?.tableHeaderRow },
		tableHeaderCell: { ...tableVariants.default.tableHeaderCell, ...tableVariants[variant]?.tableHeaderCell },
		tableHeaderSelectionBox: {
			...tableVariants.default.tableHeaderSelectionBox,
			...tableVariants[variant]?.tableHeaderSelectionBox,
		},
		tableHeaderSelectionCell: (header) => ({
			...tableVariants.default.tableHeaderSelectionCell(header),
			...tableVariants[variant]?.tableHeaderSelectionCell(header),
		}),
	};

	return (
		<>
			<Box content="div" sx={{ display: 'flex', justifyContent: 'space-between' }}>
				<SelectVisibleColumnV8 table={table} allColumns={table?.getAllColumns()} visibleColumns={[]} />

				<Flex align="center" gap=".5rem">
					{Object.keys(rowSelection)?.length > 0 && (
						<>
							<div>
								<b style={{ fontSize: '14px' }}>{Object.keys(rowSelection)?.length}</b>{' '}
								{Object.keys(rowSelection)?.length > 1 ? 'rows' : 'row'} selected
							</div>

							{!['Active', 'Worklist', 'Drafts']?.includes(selectedTabName) && (
								<Tooltip title={Object.keys(rowSelection)?.length > 1 ? 'Activate all' : 'Activate'}>
									<Button
										type="primary"
										className="animated-button"
										style={{
											color: 'white',
											padding: '8px 8px',
											fontSize: '14px',
											lineHeight: '18px',
											backgroundColor: theme.palette.lynTheme.success,
										}}
										onClick={(e) => {
											e.stopPropagation();

											activeDeactivateHandle('Inactive');
										}}
									>
										{Object.keys(rowSelection)?.length > 1 ? 'Activate all' : 'Activate'}
									</Button>
								</Tooltip>
							)}

							{!['Inactive', 'Worklist', 'Drafts']?.includes(selectedTabName) && (
								<Tooltip title={Object.keys(rowSelection)?.length > 1 ? 'Deactivate all' : 'Deactivate'}>
									<Button
										type="primary"
										className="animated-button"
										style={{
											color: '#fff',
											padding: '8px 8px',
											fontSize: '14px',
											lineHeight: '18px',
											backgroundColor: theme.palette.lynTheme.submit,
										}}
										onClick={(e) => {
											e.stopPropagation();

											activeDeactivateHandle('Active');
										}}
									>
										{Object.keys(rowSelection)?.length > 1 ? 'Deactivate all' : 'Deactivate'}
									</Button>
								</Tooltip>
							)}

							{screen === 'Lease Console' && (
								<Tooltip title={rowSelection?.length > 1 ? 'Submit all' : 'Submit'}>
									<Button
										type="primary"
										style={{
											margin: '3px',
											color: 'white',
											padding: '8px 8px',
											fontSize: '14px',
											lineHeight: '18px',
											backgroundColor: theme.palette.lynTheme.success,
										}}
										onClick={(e) => {
											e.stopPropagation();

											submitHandle();
										}}
									>
										{rowSelection?.length > 1 ? 'Submit all' : 'Submit'}
									</Button>
								</Tooltip>
							)}

							{bulkDelete && (
								<Tooltip title={rowSelection?.length > 1 ? 'Delete all' : 'Delete'}>
									<Button
										type="primary"
										className="animated-button"
										style={{
											color: 'white',
											padding: '8px 8px',
											fontSize: '14px',
											lineHeight: '18px',
											backgroundColor: theme.palette.lynTheme.delete,
										}}
										onClick={(e) => {
											e.stopPropagation();

											if (typeof deleteHandle === 'function') {
												let arr = rowSelection?.map((item) => item?.original);

												deleteHandle(arr);
											}
										}}
									>
										{rowSelection?.length > 1 ? 'Delete all' : 'Delete'}
									</Button>
								</Tooltip>
							)}
						</>
					)}

					<ExportButton handleDataExport={dataExport} />
				</Flex>
			</Box>

			<MainCard content={false} sx={styles.tableMainCard}>
				<TableContainer sx={styles.tableContainer}>
					<Table stickyHeader sx={styles.table}>
						<TableHead>
							{table.getHeaderGroups().map((headerGroup) => (
								<TableRow key={headerGroup.id} sx={styles?.tableHeaderRow}>
									{headerGroup.headers.map((header) => (
										<TableCell key={header.id} sx={styles.tableHeaderSelectionCell(header)}>
											{header.id === 'Selection' ? (
												header.isPlaceholder ? null : (
													flexRender(header.column.columnDef.header, header.getContext())
												)
											) : (
												<Box sx={styles.tableHeaderSelectionBox}>
													{header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
													<TanStackColumnSorting header={header} />
												</Box>
											)}
										</TableCell>
									))}
								</TableRow>
							))}

							{table.getHeaderGroups().map((headerGroup) => (
								<TableRow key={headerGroup.id}>
									{headerGroup.headers.map((header) => (
										<TableCell key={header.id} sx={styles.tableHeaderCell}>
											{header.column.getCanFilter() ? <TanStackColumnFilter column={header.column} /> : null}
										</TableCell>
									))}
								</TableRow>
							))}
						</TableHead>

						<TableBody
							sx={{
								'& tr:nth-of-type(even)': {
									backgroundColor: `${tableDesign.tableRowBackgroundColor.primary} !important`,
								},
							}}
						>
							{table.getRowModel().rows?.length > 0 ? (
								table.getRowModel().rows.map((row, index) => (
									<TableRow key={row.id} sx={styles.tableBodyRow}>
										{row.getVisibleCells().map((cell) => {
											return (
												<TableCell
													key={cell.id}
													title={cell.getValue()}
													sx={styles.tableBodyCell}
													onClick={() => {
														if (cell?.column?.id !== 'action') {
															onClickHandler(row.original);
														}
													}}
												>
													{flexRender(cell.column.columnDef.cell, cell.getContext())}
												</TableCell>
											);
										})}
									</TableRow>
								))
							) : (
								<TableRow>
									<TableCell colSpan={COLUMNS?.length} sx={styles.tableBodyCell}>
										<Box component="div" sx={styles.emptyTableBox}>
											<Empty />
										</Box>
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</TableContainer>

				<Box sx={styles.paginationBox}>
					<TranStackPagination table={table} persist={false} sx={styles.pagination} />
				</Box>
			</MainCard>
		</>
	);
};

export default ComptrollerDataTableV8;
